import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Flex,
  message,
  Space,
  Spin,
  Table,
  Button,
  Row,
  Col,
  Progress,
  Drawer,
  Modal
} from "antd";
import "./component/index.scss";
import api from "@/utils/api";
import http from "@/utils/http";
import commonFn from "@/utils/commonFn";
import { formatDateText } from "@/utils/date";
import { formatAmount } from "@/components/formatter";

const FinancialDashboard = () => {
  const [loading, setLoading] = useState(false);
  // const [firstWeekData, setFirstWeekData] = useState([]);
  // const [otherWeeksData, setOtherWeeksData] = useState([]);
  const [allTableData, setAllTableData] = useState([]);
  const [moreLoading, setMoreLoading] = useState(false);
  const [currentWeekPage, setCurrentWeekPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerData, setDrawerData] = useState([]);
  const [drawerTitle, setDrawerTitle] = useState("");
  const [drawerLoading, setDrawerLoading] = useState(false);
  const rowOptions = [
    {
      label: "Tenant Cash Received",
      value: "tenant",
      type: "rpsTenantStatement",
    },
    {
      label: "Past Tenant Cash Received",
      value: "past",
      type: "rpsPastTenantCashReceived",
    },
    {
      label: "Property Cash Received",
      value: "property",
      type: "rpsCheckOnly",
    },
    {
      label: "Credit Check Cash Received",
      value: "creditCheck",
      type: "rpsCreditCheck",
    },
    { label: "Total", value: "total", type: "total" },
  ];

  const [selectedItems, setSelectedItems] = useState(
    rowOptions.map((opt) => opt.value),
  );
  const [rpsData, setRpsData] = useState({
    grandTotal: 0,
    typeTotals: { rpsTenantStatement: 0, rpsCreditCheck: 0, rpsCheckOnly: 0 },
    total: [],
  });

  const fixedWeekOrder = [
    "Saturday",
    "Friday",
    "Thursday",
    "Wednesday",
    "Tuesday",
    "Monday",
    "Sunday",
  ];

  // 通用行构建函数
const buildRows = (weekData, weekIndex, isFirstWeek = true) => {
  if (!weekData?.length) return [];

  const dataMap = new Map(weekData.map((item) => [item.day, item]));
  const rows = [];

  // 字段映射
  const amountFieldMap = {
    tenant: 'tenantAmount',
    past: 'pastAmount',
    creditCheck: 'creditAmount',
    property: 'propertyAmount',
    total: 'totalAmount',
  };

  // 日期行
  const dateRow = {
    key: `week${weekIndex}_date`,
    entity: "",
    isDateRow: true,
    weekIndex,
    dates: {},
  };
  fixedWeekOrder.forEach((day, idx) => {
    const data = dataMap.get(day);
    dateRow[`day_${idx}`] = data ? formatDateText(data.date) : "";
    dateRow.dates[`day_${idx}`] = data?.date || null;
  });
  rows.push(dateRow);

  // 数据行
  rowOptions.forEach((opt) => {
    if (!selectedItems.includes(opt.value)) return;
    const row = {
      key: `week${weekIndex}_${opt.value}`,
      entity: opt.label,
      type: opt.type,
      isDateRow: false,
      isTotal: opt.value === "total",
      weekIndex,
      amounts: {},
      dates: {},
    };

    fixedWeekOrder.forEach((day, idx) => {
      const data = dataMap.get(day);
      const amountField = amountFieldMap[opt.value];
      const amount = data?.[amountField] || 0;
      row[`day_${idx}`] = amount
        ? commonFn.amountToDollarBracketFn(amount)
        : "-";
      row.amounts[`day_${idx}`] = amount;
      row.dates[`day_${idx}`] = data?.date || null;
    });

    rows.push(row);
  });

  return rows;
};
  const processAllWeeksData = (data) => {
    const modules = {
      tenant: data.rpsTenantStatement,
      past: data.rpsPastTenantCashReceived,
      creditCheck: data.rpsCreditCheck,
      property: data.rpsCheckOnly,
      total: data.total,
    };

    // let firstWeekRows = [],
    //   otherWeeksRows = [];
    let allRows = [];
    for (let weekNum = 1; weekNum <= 4; weekNum++) {
      const weekKey = `week${weekNum}`;
      const weekData = modules.total?.[weekKey];
      if (!weekData?.length) continue;

      const weekItems = weekData.map((dayItem) => {
        const date = dayItem.date;
        return {
          date,
          day: dayItem.day,
          tenantAmount: modules.tenant?.[weekKey]?.find((item) => item.date === date) ?.amount || 0,
          creditAmount: modules.creditCheck?.[weekKey]?.find((item) => item.date === date)?.amount || 0,
          propertyAmount:  modules.property?.[weekKey]?.find((item) => item.date === date)?.amount || 0,
          pastAmount:modules.past?.[weekKey]?.find((item) => item.date === date) ?.amount || 0,
          totalAmount: dayItem.amount,
        };
      });

      const rows = buildRows(weekItems, weekNum, weekNum === 1);
      // if (weekNum === 1) firstWeekRows = rows;
      // else otherWeeksRows.push(...rows);
      allRows.push(...rows);
    }
    setAllTableData(allRows); 
    // setFirstWeekData(firstWeekRows);
    // setOtherWeeksData(otherWeeksRows);
  };

  const fetchRPSWeekReport = async () => {
    setLoading(true);
    setCurrentWeekPage(1);
    setHasMore(true);
    try {
      const res = await http.post(api.getRPSShowWeekInit, {});
      if (res?.code !== "1") {
        message.error(res?.msg);
        return;
      }
      processAllWeeksData(res.data);
    } catch (error) {
      message.error("Failed to load data, please try again later");
    } finally {
      setLoading(false);
    }
  };

  const loadMoreWeeks = async () => {
    if (moreLoading || !hasMore) return;
    setMoreLoading(true);
    try {
      const res = await http.post(
        `${api.getRPSShowWeekMore}?week=${currentWeekPage}`,
        {},
      );
      if (res?.code !== "1") {
        message.error(res?.msg);
        return;
      }

      const data = res.data;
      const modules = {
        tenant: data.rpsTenantStatement,
        past: data.rpsPastTenantCashReceived,
        creditCheck: data.rpsCreditCheck,
        property: data.rpsCheckOnly,
        total: data.total,
      };

      const newRows = [];
      for (const weekKey of Object.keys(modules.total || {})) {
        const weekData = modules.total[weekKey];
        if (!weekData?.length) continue;

        const weekItems = weekData.map((dayItem) => {
          const date = dayItem.date;
          return {
            date,
            day: dayItem.day,
            tenantAmount:
              modules.tenant?.[weekKey]?.find((item) => item.date === date)
                ?.amount || 0,
            creditAmount:
              modules.creditCheck?.[weekKey]?.find((item) => item.date === date)
                ?.amount || 0,
            propertyAmount:
              modules.property?.[weekKey]?.find((item) => item.date === date)
                ?.amount || 0,
            pastAmount:
              modules.past?.[weekKey]?.find((item) => item.date === date)
                ?.amount || 0,
            totalAmount: dayItem.amount,
          };
        });
                const weekNumber = parseInt(weekKey.replace("week", ""));
        newRows.push(...buildRows(weekItems, weekNumber, false));
      }

      if (newRows.length) {
        // setOtherWeeksData((prev) => [...prev, ...newRows]);
        setAllTableData((prev) => [...prev, ...newRows]);
        setCurrentWeekPage((prev) => prev + 1);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Failed to load more weeks:", error);
      message.error("Request failed");
    } finally {
      setMoreLoading(false);
    }
  };

const handleAmountClick = (value, record, day, dayIndex) => {

  if (record.isTotal) return;

  if (record.type !== "rpsTenantStatement") return;
  
  const date = record.dates?.[`day_${dayIndex}`];
  const moduleType = record.type;
  
  const titleMap = {
    rpsTenantStatement: "Tenant Cash Received",
    // rpsPastTenantCashReceived: "Past Tenant Cash Received",
    // rpsCreditCheck: "Credit Check Cash Received",
    // rpsCheckOnly: "Property Cash Received",
  };
  const displayTitle = `${titleMap[moduleType] || moduleType} - ${date}`;
  setDrawerTitle(displayTitle);
  setDrawerVisible(true);
  fetchAmountDetail(moduleType, date);
};

const AmountCell = ({ value, record, day, idx }) => {
  const isClickable = !record.isTotal && record.type === "rpsTenantStatement";
  return (
    <div
      style={{
        textAlign: "right",
        cursor: isClickable ? "pointer" : "default",
        color: isClickable ? "#1890ff" : "inherit",
      }}
      onClick={() => isClickable && handleAmountClick(value, record, day, idx)}
    >
      {value || ""}
    </div>
  );
};

  const buildColumns = (weekType) => {
    const fixedColumn = {
      title: "RPS",
      dataIndex: "entity",
      width: 220,
      align: "center",
      fixed: "left",
      render: (text, record) => (record.isDateRow ? null : text),
    };

    const dayColumns = fixedWeekOrder.map((day, idx) => ({
      title: day,
      dataIndex: `day_${idx}`,
      key: `day_${idx}`,
      align: "center",
      width: 130,
      render: (value, record) =>
        record.isDateRow ? (
          <span style={{ fontWeight: "600" }}>{value || ""}</span>
        ) : (
          <AmountCell value={value} record={record} day={day} idx={idx} />
        ),
    }));

    return [fixedColumn, ...dayColumns];
  };

  const fetchRPSShowYear = async () => {
    try {
      const res = await http.post(api.getRPSShowYear, {});
      if (res?.code === "1") {
        setRpsData({
          grandTotal: res.data.grandTotal || 0,
          typeTotals: {
            rpsTenantStatement: res.data.typeTotals?.rpsTenantStatement || 0,
            rpsCreditCheck: res.data.typeTotals?.rpsCreditCheck || 0,
            rpsCheckOnly: res.data.typeTotals?.rpsCheckOnly || 0,
            rpsPastTenantCashReceived:res.data.typeTotals?.rpsPastTenantCashReceived || 0,
          },
          total: res.data.monthly?.total || [],
        });
      }
    } catch (error) {
      console.error("Failed to load RPS report:", error);
    }
  };

  useEffect(() => {
    const initData = async () => {
      setPageLoading(true);
      await Promise.all([fetchRPSWeekReport(), fetchRPSShowYear()]);
      setPageLoading(false);
    };
    initData();
  }, []);

  // 添加获取详情接口的函数
const fetchAmountDetail = async (type, date1) => {
  setDrawerLoading(true);
  try {
    const res = await http.get(api.getTotalAmountShow, {
      params: { type, date1 }
    });
    if (res?.code === "1") {
      setDrawerData(res.data || []);
    } else {
      message.error(res?.msg);
      setDrawerData([]);
    }
  } catch (error) {
    console.error("Failed to load detail:", error);
    setDrawerData([]);
  } finally {
    setDrawerLoading(false);
  }
};

  const columns = buildColumns();
  const totalWidth = 60 + 220 + 7 * 130;
  return (
    <div className="financial-dashboard">
      <Spin spinning={pageLoading} tip="Loading...">
        <Row gutter={[16, 16]}>
          <Col span={18}>
            {/* {firstWeekData.length > 0 && (
              <Table
                columns={columns}
                dataSource={firstWeekData}
                rowClassName={(record) => {
                  if (record.isDateRow) return "date-row";
                  if (record.isTotal) return "total-row";
                  return "";
                }}
                pagination={false}
                size="small"
                scroll={{ x: totalWidth }}
                bordered
                style={{ marginBottom: 24 }}
              />
            )}
            {otherWeeksData.length > 0 && (
              <div className="other-weeks-container">
                <Table
                  columns={columns}
                  dataSource={otherWeeksData}
                  rowClassName={(record) => {
                    if (record.isDateRow) return "date-row";
                    if (record.isTotal) return "total-row";
                    return "";
                  }}
                  pagination={false}
                  size="small"
                  scroll={{ x: totalWidth, y: 700 }}
                  bordered
                />
                <div style={{ textAlign: "center", marginTop: 16 }}>
                  <Button
                    onClick={loadMoreWeeks}
                    loading={moreLoading}
                    disabled={!hasMore}
                    type="primary"
                  >
                    {moreLoading ? "Loading..." : "More"}
                  </Button>
                </div>
              </div>
            )} */}
            <Table
         columns={columns}
         dataSource={allTableData}
         rowClassName={(record) => {
         if (record.isDateRow) return "date-row";
         if (record.isTotal) return "total-row";
         return "";
         }}
         pagination={false}
         size="small"
         scroll={{  x: "max-content",  y: "calc(100vh - 300px)",  }}
          bordered/>
         <div style={{ textAlign: "center", marginTop: 16 }}>
  <Button
    onClick={loadMoreWeeks}
    loading={moreLoading}
    disabled={!hasMore}
    type="primary"
  >
    {moreLoading ? "Loading..." : "More"}
  </Button>
</div>
          </Col>

          <Col span={6}>
            <div className="right-sidebar">
              <div
                style={{
                  background: "#FBFCFD",
                  borderRadius: "12px",
                  padding: "20px 20px 0 20px",
                }}
              >
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ marginBottom: 16 }}
                >
                  <span style={{ fontSize: "16px", color: "#0E172B" }}>
                    Total Cash Received (This year)
                  </span>
                </Flex>
                <Flex justify="space-between" align="center">
                  <div className="total-value">
                    {formatAmount(rpsData.grandTotal)}
                  </div>
                  <img
                    src={require("@/assets/images/icons/money_5.png")}
                    alt="money"
                    style={{ width: 54, height: 54 }}
                  />
                </Flex>
              </div>

              <div
                style={{
                  background: "#FBFCFD",
                  borderRadius: "10px",
                  border: "1px solid #E2E8F0",
                  padding: "16px",
                  margin: "16px",
                }}
              >
                <Flex vertical gap={16}>
                  <Flex justify="space-between">
                    <span>Tenant Cash Received</span>
                    <span>
                      {formatAmount(rpsData.typeTotals?.rpsTenantStatement)}
                    </span>
                  </Flex>
                  <Flex justify="space-between">
                    <span>Past Tenant Cash Received</span>
                    <span>
                      {formatAmount(rpsData.typeTotals?.rpsPastTenantCashReceived)}
                    </span>
                  </Flex>
                  <Flex justify="space-between">
                    <span>Credit Check Cash Received</span>
                    <span>
                      {formatAmount(rpsData.typeTotals?.rpsCreditCheck)}
                    </span>
                  </Flex>
                  <Flex justify="space-between">
                    <span>Property Cash Received</span>
                    <span>
                      {formatAmount(rpsData.typeTotals?.rpsCheckOnly)}
                    </span>
                  </Flex>
                </Flex>
              </div>

              <div style={{ padding: "0 20px 20px 20px" }}>
                <Flex align="center" gap={12} style={{ marginBottom: 20 }}>
                  <img
                    src={require("@/assets/images/icons/date.png")}
                    alt="date"
                    style={{ width: 24, height: 24 }}
                  />
                  <span style={{ fontSize: "16px", fontWeight: 500 }}>
                    Monthly Cash Received (Last 12 Months)
                  </span>
                </Flex>
                <Flex vertical gap={16}>
                  {rpsData.total?.map((item, index) => (
                    <div key={index}>
                      <div style={{ marginBottom: 4 }}>{item.month}</div>
                      <Flex align="center" gap={12}>
                        <Progress
                          percent={Math.abs(item.percent)}
                          showInfo={false}
                          strokeColor={item.percent < 0 ? "#EF4444" : "#2563EB"}
                          size="small"
                          style={{ flex: 1 }}
                        />
                        <span>{formatAmount(item.amount)}</span>
                      </Flex>
                    </div>
                  ))}
                </Flex>
              </div>
            </div>
          </Col>
        </Row>
      </Spin>
   {/* <Drawer
  title={drawerTitle}
  placement="right"
  onClose={() => setDrawerVisible(false)}
  open={drawerVisible}
  width={600}
  loading={drawerLoading}
  closable={true}
  maskClosable={false}
>
  <Table
    dataSource={drawerData}
    rowKey="batch"
    pagination={false}
    className="typeAmount"
    columns={[
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        render: (amount) => commonFn.amountToDollarBracketFn(amount),
      },
    ]}
  />
</Drawer> */}
<Modal
  title={drawerTitle}
  open={drawerVisible}
  onCancel={() => setDrawerVisible(false)}
  footer={null}
  width='50%'
  loading={drawerLoading}
  styles={{ body: { padding: 0 }, content: { minHeight: 400 } }} 
  centered 
  maskClosable={false}
>
  <Table
    dataSource={drawerData}
    rowKey={(record) => `${record.batch}_${record.name}`} 
    pagination={false}
    size="small"
    className="typeAmount"
    scroll={{ y: 850 }}  
    columns={[
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: 200,
        render: (amount) => commonFn.amountToDollarBracketFn(amount),
      },
    ]}
  />
</Modal>
    </div>
  );
};

export default FinancialDashboard;
